import * as Badge from '@studio/design-system/badge';
import type { Device } from '@studio/models/device';
import { DataSTag, Sel } from '#~/stag/tag.js';
import { setDefaultConnectionTab } from '#~/video/components/settings/iris-connection/default-tab-signal.js';
import { ConfigBadgeStatus } from '#~/video/components/config-badge/index.js';
import type { SettingsTab } from '#~/video/components/config-badge/index.js';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { DeviceHandler } from '#~/device/index.js';
import type { Severity } from '@studio/design-system/messaging';

/** Grace period for secondary control/calibration hints while runtime state settles. */
const CONFIGURE_BADGE_DELAY_MS = 5000;

/** Badge display configuration per status. */
const BADGE_CONFIG: Record<
  ConfigBadgeStatus,
  { icon: string; message: string; severity: Severity; tab: SettingsTab }
> = {
  [ConfigBadgeStatus.AUTHENTICATE]: {
    icon: 'lock',
    message: 'Authenticate',
    severity: 'warning',
    // Credentials live on the Authentication sub-tab (the canonical auth
    // deep-link target — see skip-reason-to-tab.ts / iris-connection-section.tsx),
    // NOT the Video tab. Opening 'video' landed the user on the wrong content.
    tab: 'authentication'
  },
  [ConfigBadgeStatus.SELECT_DRIVER]: {
    icon: 'settings',
    // Same verb as the canvas chip and alert; the `select_driver` key stays the e2e STag value.
    message: 'Select model',
    severity: 'warning',
    tab: 'driver'
  },
  [ConfigBadgeStatus.CONNECT_CONTROLS]: {
    icon: 'joystick',
    message: 'Connect controls',
    severity: 'info',
    tab: 'control'
  },
  [ConfigBadgeStatus.CALIBRATE_CAMERA]: {
    icon: 'target',
    message: 'Calibrate camera',
    severity: 'info',
    tab: 'monitoring'
  }
};

interface ConfigureBadgeProps {
  /** The device ID. */
  deviceId: Device['id'];
  /** Shared with the tile's transport-badge suppression. */
  configBadge: ConfigBadgeStatus | undefined;
  /** Handler to open device settings. */
  onSettings: DeviceHandler;
  /** Handler to start calibration. */
  onCalibrate?: DeviceHandler;
  /** Whether the card is collapsed (multi-view left tray). Hides badge when true. */
  isCollapsed?: boolean;
}

/**
 * Shows an actionable configuration badge on camera tiles that need setup.
 * Replaces the Phase 1 generic "Configure" badge with specific badge types:
 * Authenticate, Select model, Connect controls, Calibrate camera.
 *
 * Positioned top-right. Auth and driver actions appear immediately; secondary
 * hints wait for runtime state to settle.
 */
export const ConfigureBadge = ({
  deviceId,
  configBadge,
  onSettings,
  onCalibrate,
  isCollapsed
}: ConfigureBadgeProps): ReactElement | null => {
  const [ready, setReady] = useState(false);
  // Delay showing the badge so runtime badges have time to display first.
  useEffect(() => {
    setReady(false);
    const timer = setTimeout(() => setReady(true), CONFIGURE_BADGE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [deviceId]);

  // Explicit auth/driver problems require action now; only secondary setup hints get a grace period.
  const needsSetup =
    configBadge === ConfigBadgeStatus.AUTHENTICATE ||
    configBadge === ConfigBadgeStatus.SELECT_DRIVER;
  if (!ready && !needsSetup) return null;

  // Hide badges in multi-view left tray — they render in the content area instead.
  if (isCollapsed) return null;

  // No config badge needed.
  if (!configBadge) return null;

  const config = BADGE_CONFIG[configBadge];

  return (
    // Additive test hook (no render-condition or copy change): the tag is the
    // badge status and the uniqueID is the device id, so an e2e can assert which
    // config badge a tile shows. Per playwright-stag-selectors-only
    // (ai.knowledge/platform): badges are addressed by STag, not text/CSS.
    <div
      className='absolute right-8 top-8 z-10'
      {...DataSTag(Sel.MainRoomConfigBadge, configBadge, deviceId)}
    >
      <Badge.Root
        message={config.message}
        severity={config.severity}
        icon={config.icon}
        onClick={(e) => {
          e.stopPropagation();

          // "Calibrate camera" triggers the calibration flow directly
          if (configBadge === ConfigBadgeStatus.CALIBRATE_CAMERA && onCalibrate) {
            onCalibrate(deviceId);
            return;
          }

          // All other badges deep-link to the appropriate settings tab
          setDefaultConnectionTab(config.tab);
          onSettings(deviceId);
        }}
      />
    </div>
  );
};
